import React, { useEffect, useRef, useState } from "react";

const SIZE = 120;
const STROKE = 10;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * A circular "pressure gauge" skill meter. The fill sweep is driven by the
 * SVG `stroke-dashoffset` attribute (transitioned via CSS), not `transform`
 * — deliberately, to stay clear of the transform-attribute/CSS-animation
 * interaction documented in GearsBackground.js. The tick marks use a static
 * (never animated) `transform="rotate()"` attribute, which is safe.
 */
function SkillGauge({ name, level }) {
  const pct = Math.max(0, Math.min(100, parseInt(level, 10) || 0));
  const ref = useRef(null);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setAnimate(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimate(true);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const shownPct = animate ? pct : 0;
  const offset = CIRCUMFERENCE * (1 - shownPct / 100);

  return (
    <div className="skill-gauge" ref={ref}>
      <div className="skill-gauge-ring">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} className="skill-gauge-svg">
          {Array.from({ length: 24 }).map((_, i) => (
            <rect
              key={i}
              x={SIZE / 2 - 1}
              y={2}
              width={2}
              height={i % 3 === 0 ? 8 : 4}
              className="skill-gauge-tick"
              transform={`rotate(${i * 15} ${SIZE / 2} ${SIZE / 2})`}
            />
          ))}
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            className="skill-gauge-track"
            strokeWidth={STROKE}
            fill="none"
          />
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            className="skill-gauge-fill"
            strokeWidth={STROKE}
            fill="none"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
          />
        </svg>
        <div className="skill-gauge-pct">{shownPct}%</div>
      </div>
      <div className="skill-gauge-name">{name}</div>
    </div>
  );
}

export default SkillGauge;
